import { FC, useEffect, useRef } from "react";

import { useAv } from "../../../utils/av";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuItem } from "mds/components/Menu";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { HandRaiseFilledIcon, HandRaiseIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentClassUserId } from "store/selectors";
import {
  selectCurrentBreakoutGroupId,
  selectInstructorClassUserIdsInCurrentBreakoutRoom,
} from "store/selectors/breakouts";
import { selectIsVirtualUserSpeaking } from "store/selectors/pubsub-virtual-class-state";
import { useListSelector } from "store/store-hooks";

const LOWER_HAND_TIMEOUT_DURATION_MS = 3000;

export const HandRaiseButton: FC<{ isMobile?: boolean; isMenuItem?: boolean }> = ({
  isMobile = false,
  isMenuItem = false,
}) => {
  const { isHandRaised, toggleHand } = useAv();
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const isSpeaking = useAppSelector((state) =>
    currentClassUserId ? selectIsVirtualUserSpeaking(state, currentClassUserId) : false,
  );
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const instructorClassUserIdsInRoom = useListSelector(
    selectInstructorClassUserIdsInCurrentBreakoutRoom,
  );
  const previousInstructorClassUserIdsInRoom = useRef<string[] | null>(null);

  useEffect(() => {
    if (!isHandRaised || !isSpeaking || !!currentBreakoutGroupId) return;
    const timeoutId = setTimeout(() => toggleHand(), LOWER_HAND_TIMEOUT_DURATION_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandRaised, isSpeaking, currentBreakoutGroupId]);

  // Breakout: lower hand shortly after an instructor joins the room
  useEffect(() => {
    if (!isHandRaised || !currentBreakoutGroupId) {
      previousInstructorClassUserIdsInRoom.current = null;
      return;
    }

    if (previousInstructorClassUserIdsInRoom.current === null) {
      previousInstructorClassUserIdsInRoom.current = instructorClassUserIdsInRoom;
      return;
    }

    const hasNewInstructorInRoom = instructorClassUserIdsInRoom.some(
      (classUserId) => !previousInstructorClassUserIdsInRoom.current?.includes(classUserId),
    );

    previousInstructorClassUserIdsInRoom.current = instructorClassUserIdsInRoom;

    if (!hasNewInstructorInRoom) return;

    const timeoutId = setTimeout(() => toggleHand(), LOWER_HAND_TIMEOUT_DURATION_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandRaised, currentBreakoutGroupId, instructorClassUserIdsInRoom]);

  const buttonText = currentBreakoutGroupId
    ? isHandRaised
      ? t("av.breakouts.called_instructor")
      : t("av.breakouts.call_instructor")
    : isHandRaised
      ? t("av.lower_hand")
      : t("av.raise_hand");

  if (isMenuItem) {
    return (
      <MenuItem eventName={isHandRaised ? "Lower hand" : "Raise hand"} onClick={toggleHand}>
        <IconText
          iconStart={isHandRaised ? <HandRaiseFilledIcon /> : <HandRaiseIcon />}
          text={buttonText}
        />
      </MenuItem>
    );
  }

  if (currentBreakoutGroupId && !isMobile) {
    return (
      <RoundedButton
        isSelected={isHandRaised}
        kind="secondary"
        title={buttonText}
        onClick={toggleHand}
      >
        <IconText
          iconStart={isHandRaised ? <HandRaiseFilledIcon /> : <HandRaiseIcon />}
          text={buttonText}
        />
      </RoundedButton>
    );
  }

  return (
    <SquareIconButton
      eventName={isHandRaised ? "Lower hand" : "Raise hand"}
      isSelected={isHandRaised}
      kind="secondary"
      size={isMobile ? "xl" : "l"}
      title={buttonText}
      onClick={toggleHand}
    >
      {isHandRaised ? <HandRaiseFilledIcon /> : <HandRaiseIcon />}
    </SquareIconButton>
  );
};
